<template>
  <Teleport to="body">
    <transition name="fade">
      <div v-if="visible" class="message-box-overlay" :class="{ 'is-dark': isDark }" @click="handleOverlayClick">
      <div class="message-box-wrapper" :class="{ 'is-dark': isDark }" @click.stop>
        <div class="message-box-glass-bg"></div>
        <div class="message-box-header">
          <div class="message-box-title">{{ title }}</div>
          <button class="message-box-close" @click="handleCancel">
            <i class="fa-solid fa-xmark"></i>
          </button>
        </div>
        
        <div class="message-box-body">
          <div v-if="type === 'prompt'" class="prompt-content">
            <div class="prompt-message">
              <component v-if="isVNode(message)" :is="renderVNode(message)" />
              <template v-else>{{ message }}</template>
            </div>
            <input 
              v-model="inputValue" 
              ref="promptInput"
              class="prompt-input" 
              :placeholder="inputPlaceholder"
              @keyup.enter="handleConfirm"
            />
          </div>
          <div v-else class="message-content">
            <div class="message-text">
              <component v-if="isVNode(message)" :is="renderVNode(message)" />
              <template v-else-if="typeof message === 'string'">
                <div v-html="formattedMessage"></div>
              </template>
              <template v-else>
                {{ String(message).includes('[object') ? '内容加载中...' : message }}
              </template>
            </div>
          </div>
        </div>
        
        <div class="message-box-footer">
          <button v-if="type !== 'alert'" class="btn-cancel" @click="handleCancel">
            {{ cancelButtonText }}
          </button>
          <button class="btn-confirm" :class="{ 'is-danger': isDanger }" @click="handleConfirm">
            {{ confirmButtonText }}
          </button>
        </div>
      </div>
    </div>
  </transition>
  </Teleport>
</template>

<script>
import eventBus from '@/utils/eventBus'
import { sanitizeHtml } from '@/utils/sanitize'

export default {
  name: 'MessageBox',
  data() {
    return {
      visible: false,
      type: 'alert', // alert, confirm, prompt
      title: '',
      message: '',
      confirmButtonText: '确定',
      cancelButtonText: '取消',
      inputValue: '',
      inputPlaceholder: '',
      callback: null,
      onConfirm: null,
      onCancel: null,
      isDanger: false,
      isDark: false
    }
  },
  computed: {
    formattedMessage() {
      if (!this.message || typeof this.message !== 'string') return ''
      return sanitizeHtml(this.message.replace(/\n/g, '<br>'))
    }
  },
  watch: {
    visible(val) {
      if (val) {
        this.lockScroll()
      } else {
        this.unlockScroll()
      }
    }
  },
  mounted() {
    this.checkDarkMode()
    eventBus.on('show-message-box', this.show)
    if (typeof MutationObserver !== 'undefined') {
      this._observer = new MutationObserver(() => {
        this.checkDarkMode()
      })
      if (document.body) {
        this._observer.observe(document.body, { attributes: true, attributeFilter: ['class'] })
      }
      if (document.documentElement) {
        this._observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] })
      }
    }
  },
  beforeUnmount() {
    eventBus.off('show-message-box', this.show)
    if (this._observer) {
      this._observer.disconnect()
    }
    this.unlockScroll()
  },
  methods: {
    checkDarkMode() {
      this.isDark = (document.body && document.body.classList.contains('dark-theme')) || 
                    (document.documentElement && document.documentElement.classList.contains('dark-theme'))
    },
    lockScroll() {
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth
      this._prevOverflow = document.body.style.overflow
      this._prevPaddingRight = document.body.style.paddingRight
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${scrollbarWidth}px`
      }
      document.body.style.overflow = 'hidden'
    },
    unlockScroll() {
      if (this._prevOverflow !== undefined) {
        document.body.style.overflow = this._prevOverflow
        this._prevOverflow = undefined
      } else {
        document.body.style.overflow = ''
      }
      if (this._prevPaddingRight !== undefined) {
        document.body.style.paddingRight = this._prevPaddingRight
        this._prevPaddingRight = undefined
      } else {
        document.body.style.paddingRight = ''
      }
    },
    isVNode(msg) {
      if (!msg || typeof msg !== 'object') return false
      // Single VNode
      if (msg.__v_isVNode === true || msg.type) return true
      // Array of siblings (Fragment-like)
      if (Array.isArray(msg) && msg.length > 0) {
        return msg.every(item => item && (item.__v_isVNode === true || typeof item === 'string'))
      }
      return false
    },
    renderVNode(vnode) {
      return {
        render() {
          return vnode
        }
      }
    },
    show(options) {
      this.checkDarkMode()
      this.type = options.type || 'alert'
      this.title = options.title || '提示'
      this.message = options.message || ''
      this.confirmButtonText = options.confirmButtonText || '确定'
      this.cancelButtonText = options.cancelButtonText || '取消'
      this.inputValue = options.inputValue || ''
      this.inputPlaceholder = options.inputPlaceholder || ''
      this.callback = options.callback || null
      this.onConfirm = options.onConfirm || null
      this.onCancel = options.onCancel || null
      this.isDanger = options.isDanger || false
      
      this.visible = true
      
      if (this.type === 'prompt') {
        this.$nextTick(() => {
          if (this.$refs.promptInput) {
            this.$refs.promptInput.focus()
          }
        })
      }
    },
    handleConfirm() {
      this.visible = false
      if (this.type === 'prompt') {
        if (this.onConfirm) this.onConfirm(this.inputValue)
      } else {
        if (this.onConfirm) this.onConfirm()
        if (this.callback) this.callback()
      }
    },
    handleCancel() {
      this.visible = false
      if (this.onCancel) this.onCancel()
    },
    handleOverlayClick() {
      if (this.type === 'alert') {
        this.handleConfirm()
      } else {
        this.handleCancel()
      }
    }
  }
}
</script>

<style scoped>
/* Base Reset for component */
.message-box-overlay *, .message-box-overlay *::before, .message-box-overlay *::after {
  box-sizing: border-box;
}

.message-box-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  max-width: 100vw;
  max-height: 100vh;
  background: rgba(0, 0, 0, 0.45);
  backdrop-filter: blur(8px);
  -webkit-backdrop-filter: blur(8px);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: var(--z-message, 7000);
  transition: all 0.3s;
  padding: 20px;
  overflow: hidden;
  box-sizing: border-box;
}

.message-box-wrapper {
  position: relative;
  width: 440px;
  max-width: 100%;
  border-radius: 28px;
  overflow: hidden;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.2);
  animation: scaleIn 0.4s cubic-bezier(0.34, 1.56, 0.64, 1);
  border: 1px solid rgba(255, 255, 255, 0.45);
  display: flex;
  flex-direction: column;
  font-family: var(--font-family-base, '爱点风雅黑', sans-serif);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

.message-box-glass-bg {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.75);
  backdrop-filter: blur(30px) saturate(190%);
  -webkit-backdrop-filter: blur(30px) saturate(190%);
  z-index: -1;
}

.message-box-wrapper::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 60px;
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.3), transparent);
  pointer-events: none;
}

.message-box-header {
  padding: 24px 28px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.message-box-title {
  font-size: 20px;
  font-weight: 800;
  color: #1d1d1f;
  font-family: var(--font-family-display, '猫啃网糖圆体', '爱点风雅黑', sans-serif);
  letter-spacing: -0.5px;
  flex: 1;
  min-width: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.message-box-close {
  background: rgba(0, 0, 0, 0.05);
  border: none;
  color: #8e8e93;
  cursor: pointer;
  padding: 8px;
  border-radius: 50%;
  transition: all 0.2s;
  font-size: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 12px;
  flex-shrink: 0;
}

.message-box-close:hover {
  background: rgba(244, 67, 54, 0.1);
  color: #f44336;
  transform: rotate(90deg);
}

.message-box-body {
  padding: 12px 28px 24px;
  overflow-y: auto;
  max-height: 60vh;
}

.message-text {
  font-size: 16px;
  color: #3a3a3c;
  line-height: 1.6;
  font-weight: 500;
  word-wrap: break-word;
}

.prompt-message {
  font-size: 15px;
  color: #3a3a3c;
  margin-bottom: 14px;
  font-weight: 600;
  word-wrap: break-word;
}

.prompt-input {
  width: 100%;
  padding: 14px 18px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 16px;
  font-size: 15px;
  transition: all 0.3s;
  outline: none;
  background: rgba(255, 255, 255, 0.5);
  font-weight: 500;
}

.prompt-input:focus {
  border-color: #409eff;
  background: #ffffff;
  box-shadow: 0 0 0 4px rgba(64, 158, 255, 0.15);
}

.message-box-footer {
  padding: 16px 28px 28px;
  display: flex;
  justify-content: flex-end;
  gap: 12px;
  flex-wrap: wrap; /* Handle multiple buttons on mobile */
}

.btn-cancel, .btn-confirm {
  padding: 12px 26px;
  border-radius: 16px;
  font-size: 15px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.23, 1, 0.32, 1);
  min-width: 100px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.btn-cancel {
  border: 1px solid rgba(0, 0, 0, 0.1);
  background: rgba(255, 255, 255, 0.4);
  color: #48484a;
}

.btn-cancel:hover {
  background: rgba(255, 255, 255, 0.8);
  border-color: rgba(0, 0, 0, 0.2);
}

.btn-confirm {
  border: none;
  background: linear-gradient(135deg, #409eff 0%, #007aff 100%);
  color: #ffffff;
  box-shadow: 0 8px 20px rgba(64, 158, 255, 0.25);
}

.btn-confirm:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 25px rgba(64, 158, 255, 0.35);
}

.btn-confirm:active {
  transform: translateY(0);
}

.btn-confirm.is-danger {
  background: linear-gradient(135deg, #ff453a 0%, #ff3b30 100%);
  box-shadow: 0 8px 20px rgba(255, 59, 48, 0.25);
}

.btn-confirm.is-danger:hover {
  box-shadow: 0 12px 25px rgba(255, 59, 48, 0.35);
}

@keyframes scaleIn {
  from {
    opacity: 0;
    transform: scale(0.95) translateY(20px);
  }
  to {
    opacity: 1;
    transform: scale(1) translateY(0);
  }
}

/* Transitions */
.fade-enter-active, .fade-leave-active { transition: opacity 0.3s; }
.fade-enter-from, .fade-leave-to { opacity: 0; }

/* Scoped Dark Mode via is-dark class */
.message-box-overlay.is-dark {
  background: rgba(0, 0, 0, 0.65);
}

.message-box-wrapper.is-dark {
  background: #232428;
  border-color: rgba(255, 255, 255, 0.12);
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.6);
}

.message-box-wrapper.is-dark::before {
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.08), transparent);
}

.message-box-wrapper.is-dark .message-box-glass-bg {
  background: rgba(32, 33, 37, 0.85);
  backdrop-filter: blur(30px) saturate(180%);
  -webkit-backdrop-filter: blur(30px) saturate(180%);
}

.message-box-wrapper.is-dark .message-box-title {
  color: #f5f5f7;
}

.message-box-wrapper.is-dark .message-box-close {
  background: rgba(255, 255, 255, 0.08);
  color: #a1a1aa;
}

.message-box-wrapper.is-dark .message-box-close:hover {
  background: rgba(244, 67, 54, 0.2);
  color: #ff5252;
}

.message-box-wrapper.is-dark .message-text,
.message-box-wrapper.is-dark .prompt-message {
  color: #e4e4e7;
}

.message-box-wrapper.is-dark .prompt-input {
  background: rgba(0, 0, 0, 0.35);
  border-color: rgba(255, 255, 255, 0.15);
  color: #f5f5f7;
}

.message-box-wrapper.is-dark .prompt-input::placeholder {
  color: #71717a;
}

.message-box-wrapper.is-dark .prompt-input:focus {
  border-color: #409eff;
  background: rgba(0, 0, 0, 0.55);
  box-shadow: 0 0 0 4px rgba(64, 158, 255, 0.25);
}

.message-box-wrapper.is-dark .btn-cancel {
  background: rgba(255, 255, 255, 0.08);
  border-color: rgba(255, 255, 255, 0.12);
  color: #d1d1d6;
}

.message-box-wrapper.is-dark .btn-cancel:hover {
  background: rgba(255, 255, 255, 0.16);
  border-color: rgba(255, 255, 255, 0.2);
  color: #ffffff;
}

/* Mobile adjustments */
@media (max-width: 480px) {
  .message-box-overlay {
    padding: 0; /* Remove padding for edge-to-edge on mobile */
    align-items: flex-end; /* Show at bottom on mobile */
  }
  
  .message-box-wrapper {
    width: 100%;
    max-width: 100%;
    margin: 0;
    border-radius: 24px 24px 0 0; /* Only top round corners on mobile */
    border-left: none;
    border-right: none;
    border-bottom: none;
  }
  
  .message-box-header {
    padding: 20px 20px 10px;
  }
  
  .message-box-body {
    padding: 10px 20px 20px;
  }
  
  .message-box-footer {
    padding: 12px 16px 20px;
    flex-direction: row; /* Keep horizontal on mobile as requested */
    gap: 12px;
  }
  
  .btn-cancel, .btn-confirm {
    flex: 1; /* Equal width for both buttons */
    margin: 0;
    padding: 12px 10px;
    font-size: 15px;
    min-width: 0; /* Allow shrinking */
  }

  @keyframes scaleIn {
    from {
      opacity: 0;
      transform: translateY(100%);
    }
    to {
      opacity: 1;
      transform: translateY(0);
    }
  }
}
</style>

<style>
/* 全局深色模式保障（当 body 具有 dark-theme 时强行覆盖） */
body.dark-theme .message-box-overlay {
  background: rgba(0, 0, 0, 0.65) !important;
}

body.dark-theme .message-box-wrapper {
  background: #232428 !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
  box-shadow: 0 25px 80px rgba(0, 0, 0, 0.6) !important;
}

body.dark-theme .message-box-glass-bg {
  background: rgba(32, 33, 37, 0.85) !important;
  backdrop-filter: blur(30px) saturate(180%) !important;
  -webkit-backdrop-filter: blur(30px) saturate(180%) !important;
}

body.dark-theme .message-box-wrapper::before {
  background: linear-gradient(to bottom, rgba(255, 255, 255, 0.08), transparent) !important;
}

body.dark-theme .message-box-title {
  color: #f5f5f7 !important;
}

body.dark-theme .message-box-close {
  background: rgba(255, 255, 255, 0.08) !important;
  color: #a1a1aa !important;
}

body.dark-theme .message-box-close:hover {
  background: rgba(244, 67, 54, 0.2) !important;
  color: #ff5252 !important;
}

body.dark-theme .message-text,
body.dark-theme .prompt-message {
  color: #e4e4e7 !important;
}

body.dark-theme .prompt-input {
  background: rgba(0, 0, 0, 0.35) !important;
  border-color: rgba(255, 255, 255, 0.15) !important;
  color: #f5f5f7 !important;
}

body.dark-theme .prompt-input::placeholder {
  color: #71717a !important;
}

body.dark-theme .prompt-input:focus {
  border-color: #409eff !important;
  background: rgba(0, 0, 0, 0.55) !important;
  box-shadow: 0 0 0 4px rgba(64, 158, 255, 0.25) !important;
}

body.dark-theme .btn-cancel {
  background: rgba(255, 255, 255, 0.08) !important;
  border-color: rgba(255, 255, 255, 0.12) !important;
  color: #d1d1d6 !important;
}

body.dark-theme .btn-cancel:hover {
  background: rgba(255, 255, 255, 0.16) !important;
  border-color: rgba(255, 255, 255, 0.2) !important;
  color: #ffffff !important;
}
</style>


